// ImportPreviewTable — REAL implementation (CHG-1260).
//
// Replaces the CHG-1247 stub at apps/dashboard-v2/src/wizards/customRenders/
// ImportPreviewTable.tsx via the registerWizardComponent registration
// updated in ./registerWizardPrimitives.ts. The stub file itself is left
// orphaned but immutable per "CHG-1247 immutable".
//
// Used by import-map wizard step "preview" (CHG-1241 manifest). Renders the
// first N (default 10) rows projected to canonical fields using the
// column_mapping from the previous step, plus a summary header showing
// total/valid/invalid/mapped counts. Per-row validation errors render as
// inline error badges in their cells. The component calls onChange(true) on
// mount so the wizard's required-not-empty validator on `__preview` passes
// (the field's existence is a "viewed and approved" semaphore — the actual
// execution happens via POST /imports/{id}/execute when the wizard submits).

import { useEffect, useMemo } from "react";
import type { CustomRenderProps } from "../customRenderRegistry";

interface PreviewRow {
  row_number?: number;
  mapped?: Record<string, string | null>;
  errors?: Record<string, string>;
  status?: "valid" | "invalid" | "executed" | "pending";
}

interface PreviewSummary {
  total_rows?: number;
  valid_rows?: number;
  invalid_rows?: number;
  mapped_rows?: number;
}

export function ImportPreviewTable(props: CustomRenderProps) {
  const { onChange } = props;

  // Wizard host sets `__preview` field's value to `true` on mount via
  // onChange — the manifest declares the field required (rule=not_empty),
  // and "viewing the preview" is the implicit confirmation gate.
  useEffect(() => {
    onChange(true);
    // Intentionally empty deps — fire-once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Forward-promised: the wizard host will eventually pass `preview_rows` +
  // `summary` via field.props after fetching from /imports/{id}/rows +
  // /imports/{id}/summary. Until then, the component renders a non-empty
  // shell explaining what will appear when the data arrives.
  const rows = useMemo<PreviewRow[]>(() => {
    const fromProps = (props as Record<string, unknown>).preview_rows;
    return Array.isArray(fromProps) ? (fromProps as PreviewRow[]) : [];
  }, [props]);

  const summary = useMemo<PreviewSummary>(() => {
    const fromProps = (props as Record<string, unknown>).summary;
    return fromProps && typeof fromProps === "object" ? (fromProps as PreviewSummary) : {};
  }, [props]);

  const columns = useMemo<string[]>(() => {
    const cols = new Set<string>();
    for (const r of rows) {
      if (r.mapped) {
        for (const k of Object.keys(r.mapped)) cols.add(k);
      }
    }
    return Array.from(cols);
  }, [rows]);

  return (
    <div data-wizard-component-id="ImportPreviewTable" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div
        style={{
          display: "flex",
          gap: 12,
          padding: "10px 14px",
          background: "var(--color-bg-subtle)",
          border: "1px solid var(--color-border-subtle)",
          borderRadius: "var(--radius-md)",
          fontSize: 12,
        }}
      >
        <SummaryStat label="Total" value={summary.total_rows} />
        <SummaryStat label="Mapped" value={summary.mapped_rows} />
        <SummaryStat label="Valid" value={summary.valid_rows} tone="success" />
        <SummaryStat label="Invalid" value={summary.invalid_rows} tone="error" />
      </div>

      {rows.length === 0 ? (
        <div
          style={{
            padding: "20px 16px",
            border: "1px dashed var(--color-border-subtle)",
            borderRadius: "var(--radius-md)",
            color: "var(--color-text-muted)",
            fontSize: 13,
            textAlign: "center",
          }}
        >
          Preview data will load from <code>/imports/{"{id}"}/rows</code> + <code>/summary</code> once
          the wizard host wires the import_batch_id. The submit button is enabled (preview viewed).
        </div>
      ) : (
        <div
          style={{
            border: "1px solid var(--color-border-subtle)",
            borderRadius: "var(--radius-md)",
            overflow: "auto",
            maxHeight: 360,
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "var(--color-bg-subtle)", position: "sticky", top: 0 }}>
                <th style={{ textAlign: "right", padding: "6px 10px", fontWeight: 600 }}>#</th>
                {columns.map((c) => (
                  <th key={c} style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600 }}>
                    {c}
                  </th>
                ))}
                <th style={{ textAlign: "left", padding: "6px 10px", fontWeight: 600 }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr
                  key={row.row_number ?? i}
                  data-row-number={row.row_number ?? i}
                  style={{
                    borderTop: "1px solid var(--color-border-subtle)",
                    background: row.status === "invalid" ? "var(--color-error-tint)" : "transparent",
                  }}
                >
                  <td style={{ textAlign: "right", padding: "6px 10px", color: "var(--color-text-muted)" }}>
                    {row.row_number ?? i + 1}
                  </td>
                  {columns.map((c) => {
                    const v = row.mapped?.[c] ?? null;
                    const err = row.errors?.[c];
                    return (
                      <td key={c} style={{ padding: "6px 10px", color: err ? "var(--color-error)" : "var(--color-text)" }}>
                        {v ?? "—"}
                        {err ? (
                          <span
                            data-error-for={c}
                            style={{
                              marginLeft: 6,
                              padding: "1px 6px",
                              borderRadius: 9999,
                              fontSize: 10,
                              background: "var(--color-error)",
                              color: "white",
                            }}
                          >
                            {err}
                          </span>
                        ) : null}
                      </td>
                    );
                  })}
                  <td style={{ padding: "6px 10px" }}>{row.status ?? "pending"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value, tone }: { label: string; value: number | undefined; tone?: "success" | "error" }) {
  const color = tone === "success" ? "var(--color-success)" : tone === "error" ? "var(--color-error)" : "var(--color-text)";
  return (
    <div style={{ display: "flex", flexDirection: "column", minWidth: 80 }}>
      <span style={{ color: "var(--color-text-muted)", fontSize: 11 }}>{label}</span>
      <span style={{ color, fontWeight: 600, fontSize: 14 }}>{value ?? "—"}</span>
    </div>
  );
}
